<template>
    <div class="nacex-wrapper">
        <div :class="embedded ? 'nacex-embedded' : 'custom-card'">
            <div class="card-header" v-if="!embedded">
                <div class="nacex-header p-d-flex p-ai-center">
                    <i class="pi pi-truck p-mr-2" style="color: #2196F3; font-size: 1.25rem;"></i>
                    <h3 class="p-m-0">Gestão de Envios NACEX</h3>
                    <small class="p-ml-2" style="color: #6c757d;">(*) Campos obrigatórios</small>
                </div>
            </div>
            <div v-else class="nacex-header p-mb-3">
                <i class="pi pi-truck p-mr-2" style="color: #2196F3;"></i>
                <strong>Campos NACEX</strong>
                <small class="p-ml-2" style="color: #6c757d;">(*) Campos obrigatórios</small>
            </div>

            <div class="card-body">
                <!-- Operation type, Service, Date -->
                <div class="p-fluid p-formgrid p-grid">
                    <div class="p-field p-col-12 p-md-3">
                        <label>Tipo de Operação *</label>
                        <Dropdown
                            v-model="formData.type"
                            :options="operationTypes"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Selecione o tipo"
                            @change="onTypeChange"
                        />
                    </div>
                    <div class="p-field p-col-12 p-md-3">
                        <label>Serviço NACEX *</label>
                        <Dropdown
                            v-model="formData.service"
                            :options="services"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Selecione o serviço"
                        />
                    </div>
                    <div class="p-field p-col-12 p-md-3">
                        <label>Data *</label>
                        <Calendar v-model="formData.date" dateFormat="dd/mm/yy" :showIcon="true" :disabledDays="[0,6]" />
                    </div>
                    <div class="p-field p-col-12 p-md-3" v-if="!isPickup">
                        <label>Envio com retorno</label>
                        <Dropdown
                            v-model="formData.recurrence"
                            :options="recurrenceOptions"
                            optionLabel="label"
                            optionValue="value"
                        />
                    </div>
                    <div class="p-field p-col-12 p-md-3" v-if="isPickup">
                        <label>Hora Início *</label>
                        <Calendar v-model="pickupTimes.start" timeOnly hourFormat="24" :stepMinute="30" />
                    </div>
                    <div class="p-field p-col-12 p-md-3" v-if="isPickup">
                        <label>Hora Fim *</label>
                        <Calendar v-model="pickupTimes.end" timeOnly hourFormat="24" :stepMinute="30" />
                    </div>
                </div>

                <!-- Extra pickup fields -->
                <div class="p-fluid p-formgrid p-grid" v-if="isPickup">
                    <div class="p-field p-col-12 p-md-3">
                        <label>Veículo</label>
                        <Dropdown v-model="formData.vehicle" :options="vehicles" optionLabel="label" optionValue="value" />
                    </div>
                    <div class="p-field p-col-12 p-md-4">
                        <label>Nome solicitante</label>
                        <InputText v-model="formData.requesterName" />
                    </div>
                    <div class="p-field p-col-12 p-md-5">
                        <label>E-mail solicitante</label>
                        <InputText type="email" v-model="formData.requesterEmail" />
                    </div>
                </div>

                <!-- Package info -->
                <div class="p-fluid p-formgrid p-grid">
                    <div class="p-field p-col-12 p-md-2">
                        <label>Tipo Embalagem</label>
                        <Dropdown
                            v-model="formData.packageType"
                            :options="packageTypes"
                            optionLabel="label"
                            optionValue="value"
                        />
                    </div>
                    <div class="p-field p-col-12 p-md-2">
                        <label>Volumes *</label>
                        <InputNumber v-model="formData.packages" :min="1" showButtons />
                    </div>
                    <div class="p-field p-col-12 p-md-2">
                        <label>Peso (Kg) *</label>
                        <InputNumber v-model="formData.weight" :min="0.1" :minFractionDigits="1" mode="decimal" />
                    </div>
                    <div class="p-field p-col-12 p-md-3">
                        <label>Tipo pagamento</label>
                        <Dropdown
                            v-model="formData.payment_type"
                            :options="paymentTypes"
                            optionLabel="label"
                            optionValue="value"
                        />
                    </div>
                    <div class="p-field p-col-12 p-md-3">
                        <label>Referência</label>
                        <InputText v-model="formData.reference" />
                    </div>
                </div>

                <hr class="separator" />

                <!-- Address blocks using NacexAddressBlock -->
                <div class="p-grid">
                    <div class="p-col-12 p-md-6">
                        <NacexAddressBlock
                            v-model="formData.sender"
                            :title="isPickup ? 'Destino (Empresa)' : 'Remetente'"
                            :nacex-accounts="nacexAccounts"
                            :selected-account-id="selectedAccountId"
                            @select-account="onAccountSelected"
                            :readonly="true"
                        />
                    </div>
                    <div class="p-col-12 p-md-6">
                        <NacexAddressBlock
                            v-model="formData.recipient"
                            :title="isPickup ? 'Local de Recolha (Cliente)' : 'Destinatário'"
                            :locations="{ clientData: defaultLocations.clientData, entityData: null }"
                            :nacex-accounts="nacexAccounts"
                            :prefill-only="true"
                        />
                    </div>
                </div>

                <div class="p-d-flex p-jc-end p-mt-4 button-row">
                    <Button
                        label="Cancelar"
                        icon="pi pi-times"
                        :disabled="loading"
                        class="p-button-secondary p-button-danger p-mr-2"
                        @click="resetForm"
                    />
                    <Button
                        :label="submitLabel"
                        icon="pi pi-check"
                        :disabled="loading"
                        :loading="loading"
                        class="p-button-primary"
                        @click="submitShipment"
                    />
                </div>
            </div>
        </div>

        <Dialog
            header="Sucesso"
            :visible.sync="showLabelDialog"
            :style="{ width: '50vw' }"
            modal
            :closable="false"
        >
            <div class="p-text-center" style="text-align: center;">
                <p class="p-mb-3" style="font-size: 1.1rem; margin-bottom: 1rem;">
                    <i class="pi pi-check-circle" style="color: green; font-size: 1.5rem;"></i>
                    Operação registada com sucesso! <br> <strong>ID: {{ createdShipmentId }}</strong>
                </p>

                <div v-if="pdfBase64">
                    <Button label="Imprimir Etiqueta" icon="pi pi-print" @click="downloadPdf" />
                    <br /><br />
                    <iframe :src="pdfData" width="100%" height="450px" style="border: 1px solid #ccc;"></iframe>
                </div>
                <div v-else>
                    <p>A recolha foi agendada. O estafeta levará a etiqueta.</p>
                </div>
            </div>
            <template #footer>
                <Button
                    label="Fechar"
                    icon="pi pi-times"
                    @click="closeDialogLabel"
                    class="p-button-secondary"
                />
            </template>
        </Dialog>
    </div>
</template>

<script>
import shipmentsService from '@/services/shipments.service';
import nacexAccountsService from '@/services/nacex-accounts.service';
import NacexAddressBlock from '@/components/shipment/NacexAddressBlock.vue';

export default {
    name: 'NacexShippingForm',
    components: {
        NacexAddressBlock
    },
    props: {
        patId: { type: Number, default: null },
        internalDossierId: { type: [Number, String], default: null },
        mode: {
            type: String,
            default: 'pat',
            validator: (value) => ['pat', 'internal_dossier'].includes(value)
        },
        defaultData: { type: Object, default: () => ({}) },
        embedded: { type: Boolean, default: false }
    },
    computed: {
        isInternalDossierMode() {
            return this.mode === 'internal_dossier' || (!!this.internalDossierId && !this.patId);
        },
        isPickup() {
            return this.formData.type === 'pickup';
        },
        submitLabel() {
            return this.isPickup ? 'Agendar Recolha' : 'Criar Envio';
        },
        defaultReference() {
            if (this.isInternalDossierMode && this.internalDossierId) {
                return `${this.internalDossierId}`;
            }

            if (this.patId) {
                return `${this.patId}`;
            }

            return '';
        }
    },
    data() {
        return {
            loading: false,
            showLabelDialog: false,
            createdShipmentId: null,
            pdfBase64: null,
            pdfData: null,

            nacexAccounts: [],
            selectedAccountId: null,

            packageTypes: [
                { label: 'Pacote / Caixa (Standard)', value: '2' },
                { label: 'Documentos', value: '0' },
                { label: 'Bolsa (Bag)', value: '1' }
            ],
            operationTypes: [
                { label: 'Envio (Empresa -> Cliente)', value: 'shipment' },
                { label: 'Recolha (Cliente -> Empresa)', value: 'pickup' },
                { label: 'Devolução (Etiqueta)', value: 'return' }
            ],
            services: [
                { label: 'NACEX 10:00H', value: '01' },
                { label: 'NACEX 12:00H', value: '02' },
                { label: 'NACEX 19:00H', value: '08' },
                { label: 'NACEX 08:30H', value: '11' }
            ],
            vehicles: [
                { label: 'Carro/Carrinha (Padrão)', value: 'C' },
                { label: 'Mota (Pequenos)', value: 'M' }
            ],
            paymentTypes: [
                { label: 'Origem', value: 'O' },
                { label: 'Destino', value: 'D' },
                { label: 'Terceiro', value: 'T' }
            ],
            recurrenceOptions: [
                { label: 'Sim', value: 'S' },
                { label: 'Não', value: 'N' }
            ],
            pickupTimes: {
                start: new Date(new Date().setHours(9, 0, 0)),
                end: new Date(new Date().setHours(18, 0, 0))
            },
            defaultLocations: {
                clientData: null
            },
            formData: {
                type: 'shipment',
                service: '08',
                vehicle: 'C',
                date: this.getNextBusinessDay(0),
                reference: '',
                packages: 1,
                weight: 1.0,
                packageType: '2',
                payment_type: 'O',
                recurrence: 'N',
                requesterName: 'Ricardo Vieira',
                requesterEmail: 'ricardovieira@circuitcrafters.pt',
                sender: {
                    name: '',
                    address: '',
                    zip: '',
                    city: '',
                    phone: '',
                    email: '',
                    country: 'PT',
                    observations1: '',
                    observations2: ''
                },
                recipient: {
                    name: '',
                    address: '',
                    zip: '',
                    city: '',
                    phone: '',
                    email: '',
                    country: 'PT',
                    observations1: '',
                    observations2: ''
                }
            }
        };
    },
    async mounted() {
        await this.loadNacexAccounts();
        this.initDefaultData();
    },
    methods: {
        async loadNacexAccounts() {
            try {
                this.nacexAccounts = await nacexAccountsService.getAll();
                if (this.nacexAccounts && this.nacexAccounts.length > 0 && !this.selectedAccountId) {
                    const defaultAcc = this.nacexAccounts.find(a => a.client_code === '00843') || this.nacexAccounts[0];
                    if (defaultAcc) {
                        this.onAccountSelected(defaultAcc);
                    }
                }
            } catch (err) {
                console.error("Erro ao carregar contas Nacex", err);
            }
        },
        initDefaultData() {
            this.formData.reference = this.defaultData.reference || this.defaultReference;

            if (this.defaultData && Object.keys(this.defaultData).length > 0) {
                this.defaultLocations.clientData = this.defaultData.client || null;
                if (this.defaultLocations.clientData) {
                    this.formData.recipient = {
                        ...this.formData.recipient,
                        ...this.defaultLocations.clientData
                    };
                }
            }
            this.fillInitDate();
        },
        onAccountSelected(account) {
            this.selectedAccountId = account.id;

            if (account.client_code !== '00843') {
                this.formData.payment_type = 'T';
            } else {
                this.formData.payment_type = this.isPickup ? 'D' : 'O';
            }

            this.formData.sender = {
                name: account.name || '',
                address: account.address || '',
                zip: account.zip || '',
                city: account.city || '',
                phone: account.phone || '',
                email: account.email || '',
                country: account.country || 'PT',
                observations1: this.formData.sender?.observations1 || '',
                observations2: this.formData.sender?.observations2 || ''
            };
        },
        fillInitDate() {
            if (this.isPickup) {
                this.formData.date = this.getNextBusinessDay(1);
            } else {
                this.formData.date = this.getNextBusinessDay(0);
            }
        },
        getNextBusinessDay(offsetDays) {
            const date = new Date();
            date.setDate(date.getDate() + offsetDays);
            while (date.getDay() === 0 || date.getDay() === 6) {
                date.setDate(date.getDate() + 1);
            }
            return date;
        },
        onTypeChange() {
            const currentAccount = this.nacexAccounts.find(a => a.id === this.selectedAccountId);
            if (currentAccount && currentAccount.client_code !== '00843') {
                this.formData.payment_type = 'T';
            } else {
                this.formData.payment_type = this.formData.type === 'pickup' ? 'D' : 'O';
            }

            this.fillInitDate();
        },
        formatTime(dateObj) {
            if (!dateObj) return '09:00';
            const h = dateObj.getHours().toString().padStart(2, '0');
            const m = dateObj.getMinutes().toString().padStart(2, '0');
            return `${h}:${m}`;
        },
        resetForm() {
            this.$emit('cancel');
        },
        async submitShipment() {
            this.loading = true;
            try {
                let response;
                let payload = this.preparePayload();

                if (this.formData.type === 'shipment') {
                    response = this.isInternalDossierMode
                        ? await shipmentsService.createInternalDossierShipment(payload)
                        : await shipmentsService.createShipment(payload);
                } else if (this.formData.type === 'pickup') {
                    response = this.isInternalDossierMode
                        ? await shipmentsService.scheduleInternalDossierPickup(payload)
                        : await shipmentsService.schedulePickup(payload);
                } else if (this.formData.type === 'return') {
                    response = this.isInternalDossierMode
                        ? await shipmentsService.createInternalDossierReturn(payload)
                        : await shipmentsService.createShipmentWithReturn(payload);
                }

                if (response.error) {
                    throw new Error(response.details || response.error);
                }

                if (response.success) {
                    this.createdShipmentId = response.shipment_code || response.pickup_id || response.expedition_id;
                    const label = response.label_base64 || response.outbound_label;

                    if (label) {
                        this.pdfBase64 = label;
                        this.pdfData = `data:application/pdf;base64,${label}`;
                        this.showLabelDialog = true;
                    } else {
                        this.$toast.add({
                            severity: "success",
                            summary: "Agendado",
                            detail: "Recolha criada com sucesso!",
                            life: 5000
                        });
                        this.$emit('success', this.createdShipmentId);
                    }
                }
            } catch (error) {
                const msg = error.response?.data?.error || error.message || 'Erro de comunicação';
                this.$toast.add({ severity: "error", summary: "Erro", detail: msg, life: 5000 });

                if (error.response?.data?.details) {
                    this.$toast.add({ severity: "error", summary: "Detalhes", detail: error.response.data.details, life: 7000 });
                }
            } finally {
                this.loading = false;
            }
        },
        preparePayload() {
            const d = this.formData.date;
            const formattedDate = d instanceof Date
                ? `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`
                : d;

            return {
                ...this.getContextPayload(),
                nacex_account_id: this.selectedAccountId,
                operation_type: this.formData.type,
                service_type: this.formData.service,
                reference: this.formData.reference,
                packages: this.formData.packages,
                weight: this.formData.weight,
                package_type: this.formData.packageType,
                payment_type: this.formData.payment_type,
                date: formattedDate,

                sender_name: this.formData.sender.name,
                sender_address: this.formData.sender.address,
                sender_zip: this.formData.sender.zip,
                sender_city: this.formData.sender.city,
                sender_phone: this.formData.sender.phone,
                sender_country: this.formData.sender.country || 'PT',
                sender_observations1: this.formData.sender.observations1,
                sender_observations2: this.formData.sender.observations2,

                recipient_name: this.formData.recipient.name,
                recipient_address: this.formData.recipient.address,
                recipient_zip: this.formData.recipient.zip,
                recipient_city: this.formData.recipient.city,
                recipient_phone: this.formData.recipient.phone,
                recipient_email: this.formData.recipient.email,
                recipient_country: this.formData.recipient.country || 'PT',
                recipient_observations1: this.formData.recipient.observations1,
                recipient_observations2: this.formData.recipient.observations2,

                pickup_time_start: this.isPickup ? this.formatTime(this.pickupTimes.start) : null,
                pickup_time_end: this.isPickup ? this.formatTime(this.pickupTimes.end) : null,
                vehicle: this.formData.vehicle,
                requester_name: this.formData.requesterName,
                requester_email: this.formData.requesterEmail,

                recurrence: this.formData.recurrence
            };
        },
        downloadPdf() {
            const link = document.createElement("a");
            link.href = this.pdfData;
            link.download = `Nacex_${this.createdShipmentId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        closeDialogLabel() {
            this.showLabelDialog = false;
            this.$emit('cancel');
        },
        getContextPayload() {
            if (this.isInternalDossierMode) {
                return {
                    internal_dossier_id: this.internalDossierId
                };
            }

            return {
                pat_id: this.patId
            };
        }
    }
};
</script>

<style scoped>
.nacex-wrapper {
    width: 100%;
}

.nacex-embedded {
    border: 1px solid #ced4da;
    border-radius: 6px;
    padding: 1rem;
    background-color: #f8f9fa;
}

.custom-card {
    background-color: #ffffff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    margin-bottom: 2rem;
}

.card-header {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #dee2e6;
    background-color: #f8f9fa;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
}

.nacex-header {
    font-size: 1rem;
    padding-bottom: 0.5rem;
}

.card-body {
    padding: 1.25rem;
}

.separator {
    border: 0;
    border-top: 1px solid #dee2e6;
    margin: 1rem 0;
}

.p-field {
    margin-bottom: 1rem;
}

.p-field label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #495057;
}

.button-row {
    border-top: 1px solid #dee2e6;
    padding-top: 1rem;
    margin-top: 1rem;
}
</style>
